// frontend/src/components/BioGenerator.js
//
// The bio generator embedded on /tools/instagram-bio-generator and siblings.
//
// THE SERP IS NINE ESTABLISHED SaaS TOOLS running the identical lead magnet —
// Hootsuite, Mention, ContentStudio, Postiz, RADAAR, Birdeye, Blotato, Storrito.
// We do not beat them on domain authority. We beat them on the three things
// every one of them gets wrong:
//
//   1. THE LIMIT. They hand you bios and let you discover yours does not fit.
//      Ours are measured server-side and only fitting options are returned; the
//      count is shown per option. (Tested: gpt-4o-mini put 3 of 5 TikTok bios
//      over the 80-char limit. Models cannot count characters.)
//   2. THE PREVIEW. A bio is read on a profile, not in a text box. Line breaks
//      and emoji change how it looks; this shows the finished profile.
//   3. THE PASTE. Bios are multi-line and copying them badly is the moment the
//      user gets annoyed. Copy preserves line breaks exactly.

import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Row, Col, Form, Button, Spinner, ProgressBar } from 'react-bootstrap'
import { Stars, Clipboard, ClipboardCheck, ExclamationTriangleFill, PersonCircle } from 'react-bootstrap-icons'

const PLATFORMS = [
  ['instagram', 'Instagram', 150, 'bio'],
  ['tiktok',    'TikTok',     80, 'bio'],
  ['linkedin',  'LinkedIn',  220, 'headline'],
]
const TONES = [
  ['professional', 'Professional'], ['playful', 'Playful'], ['minimal', 'Minimal'],
  ['bold', 'Bold'], ['warm', 'Warm'], ['aesthetic', 'Aesthetic'],
]

const meta = p => PLATFORMS.find(x => x[0] === p) || PLATFORMS[0]

// The name generators' "Write a matching bio" step links here with
// ?name=<the name they copied>&style=<its style> (Growth round, T21). Their
// styles map onto our tones; a tone name is accepted as-is.
const STYLE_TO_TONE = { clean: 'minimal', business: 'professional', playful: 'playful', aesthetic: 'aesthetic' }

export function readPrefill (search) {
  const q = new URLSearchParams(search || '')
  const name = (q.get('name') || '').replace(/[\r\n]+/g, ' ').trim().slice(0, 60)
  const style = (q.get('style') || '').trim().toLowerCase()
  const tone = TONES.some(([v]) => v === style) ? style : STYLE_TO_TONE[style] || ''
  // How the name would read as a handle in the preview: 'The Luna Hour' → thelunahour.
  const handle = name.toLowerCase().replace(/^@/, '').replace(/[^a-z0-9._]/g, '').slice(0, 30)
  // Goes first in the description so the bios are written for that name.
  const intro = !name ? '' : `${/\s|^@/.test(name) ? '' : '@'}${name} — `
  return { name, tone, handle, intro }
}

export default function BioGenerator ({ platform = 'instagram' }) {
  const { search } = useLocation()
  const [prefill] = useState(() => readPrefill(search))
  const [form, setForm] = useState({
    business: prefill.intro, platform, tone: prefill.tone || 'professional', emoji: true, cta: true,
  })
  const [result, setResult]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [capped, setCapped]   = useState(false)
  const [copied, setCopied]   = useState(-1)

  const [, label, max, field] = meta(form.platform)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    if (!form.business.trim() || form.business.trim() === prefill.intro.trim()) {
      setError('Tell us what the account is about first.'); return
    }
    setLoading(true); setError(''); setResult(null); setCopied(-1)
    try {
      const r = await fetch('/api/public/ai/bio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',          // the 3-per-session limit needs the cookie
        body: JSON.stringify(form),
      })
      const data = await r.json()
      if (!r.ok) { setError(data.error || 'Something went wrong.'); setCapped(!!data.limitReached); return }
      setResult(data)
    } catch {
      setError('Could not reach the server. Try again in a moment.')
    } finally { setLoading(false) }
  }

  // Line breaks must survive the clipboard — that is the whole point.
  const copy = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(i); setTimeout(() => setCopied(-1), 2200)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text; document.body.appendChild(ta); ta.select()
      try { document.execCommand('copy'); setCopied(i); setTimeout(() => setCopied(-1), 2200) } catch { /* ignore */ }
      document.body.removeChild(ta)
    }
  }

  const barVariant = pct => pct > 92 ? 'warning' : pct > 75 ? 'info' : 'success'

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>
        <Stars className='text-primary me-2' />
        Generate your {label} {field}
      </h2>
      <p className='text-muted mb-4'>
        Every option is checked against the real {max}-character limit before you see it, so
        nothing you copy gets cut off. Free, no sign-up.
      </p>

      <Form onSubmit={submit}>
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold small'>
            What is the account about? <span className='text-danger'>*</span>
          </Form.Label>
          <Form.Control
            as='textarea' rows={2} maxLength={300}
            placeholder='e.g. independent hair salon in Leeds, balayage and curly cuts, women 25-45'
            value={form.business} onChange={e => set('business', e.target.value)}
          />
          <Form.Text className='text-muted' style={{ fontSize: 12 }}>
            {prefill.name
              ? <>We filled in “{prefill.name}” from the name generator. Add what the account is about after the dash.</>
              : 'Say what you do and who for. The more specific, the less generic the bios.'}
          </Form.Text>
        </Form.Group>

        <Row className='g-3 mb-3'>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Platform</Form.Label>
            <Form.Select value={form.platform} onChange={e => set('platform', e.target.value)}>
              {PLATFORMS.map(([v, l, m]) => <option key={v} value={v}>{l} ({m})</option>)}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Tone</Form.Label>
            <Form.Select value={form.tone} onChange={e => set('tone', e.target.value)}>
              {TONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Form.Select>
          </Col>
          <Col md={4} className='d-flex align-items-end'>
            <div className='d-flex gap-3 pb-1'>
              <Form.Check type='switch' id='bio-emoji' label='Emoji'
                          checked={form.emoji} onChange={e => set('emoji', e.target.checked)} />
              <Form.Check type='switch' id='bio-cta' label='Call to action'
                          checked={form.cta} onChange={e => set('cta', e.target.checked)} />
            </div>
          </Col>
        </Row>

        <Button type='submit' variant='primary' size='lg' disabled={loading}>
          {loading
            ? <><Spinner size='sm' animation='border' className='me-2' />Writing your {field}s…</>
            : `Generate ${label} ${field}s`}
        </Button>
        {!result && <span className='text-muted small ms-3'>Free · no sign-up · every option fits</span>}
      </Form>

      {error && (
        <div className='mt-4 p-3 rounded-3 d-flex gap-2'
             style={{ background: capped ? '#eff6ff' : '#f8ecE8', border: `1px solid ${capped ? '#bfdbfe' : '#e8c4b8'}` }}>
          <ExclamationTriangleFill className={capped ? 'text-primary mt-1' : 'text-danger mt-1'} />
          <div>
            <div className='small'>{error}</div>
            {capped && (
              <Button size='sm' variant='primary' href='/register' className='mt-2'>
                Start using Greatly Social free
              </Button>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className='mt-4'>
          {/* ── THE PREVIEW: a bio is read on a profile, not in a text box ── */}
          <div className='mb-4 p-3 rounded-3' style={{ background: '#fafafa', border: '1px solid #e5e7eb' }}>
            <div className='text-muted small text-uppercase mb-2' style={{ letterSpacing: '.04em' }}>
              How the first one looks on your profile
            </div>
            <div className='d-flex gap-3 align-items-start'>
              <PersonCircle size={54} className='text-secondary flex-shrink-0' />
              <div style={{ minWidth: 0 }}>
                <div className='fw-semibold'>{prefill.handle || 'yourhandle'}</div>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                  {result.bios[0].text}
                </div>
                <div className='small text-primary mt-1'>yourlink.com</div>
              </div>
            </div>
          </div>

          {result.bios.map((b, i) => {
            const pct = Math.round((b.length / result.max) * 100)
            return (
              <div key={i} className='mb-3 p-3 rounded-3' style={{ border: '1px solid #e5e7eb' }}>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{b.text}</div>
                <div className='d-flex align-items-center gap-3 mt-2'>
                  <div style={{ width: 110 }}>
                    <ProgressBar now={pct} variant={barVariant(pct)} style={{ height: 6 }} />
                  </div>
                  <span className='small text-muted' style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {b.length} / {result.max}
                  </span>
                  <Button size='sm' variant={copied === i ? 'success' : 'outline-secondary'}
                          onClick={() => copy(b.text, i)} className='ms-auto'>
                    {copied === i
                      ? <><ClipboardCheck className='me-1' />Copied</>
                      : <><Clipboard className='me-1' />Copy</>}
                  </Button>
                </div>
              </div>
            )
          })}

          <p className='text-muted small'>
            Line breaks are preserved when you copy. Paste straight into the {field} field —
            if a break disappears, press return manually at that point and the rest will hold.
          </p>

          {/* The bridge: they have a bio, and the link under it is the next problem. */}
          <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
            <h3 className='fw-bold h5 mb-2'>You get one link under that bio</h3>
            <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
              At Greatly Social you can turn it into a page holding everything you share — and it
              updates itself with your latest posts, so it never goes stale.
            </p>
            <Button variant='light' href='/register' className='fw-semibold me-2 mb-2'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-light' href='/link-in-bio' className='mb-2'>
              See link in bio
            </Button>
          </div>

          {typeof result.remaining === 'number' && (
            <p className='text-muted small mt-3 mb-0'>
              {result.remaining > 0
                ? `${result.remaining} more free ${result.remaining === 1 ? 'set' : 'sets'} this session.`
                : 'That was your last free set this session.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
